import { createSlice } from '@reduxjs/toolkit';
import {
  fetchPointsThunk,
  addPointsThunk,
  createBadgeThunk,
  assignBadgeThunk,
  fetchUserBadgesThunk,
  fetchBadgeProgressThunk,
  fetchLeaderboardThunk,
  deleteBadgeThunk
} from './gamificationActionThunks';

const initialState = {
  points: {}, // Store user points as { userId: pointsData }
  badges: {}, // Store user badges as { userId: [badgeList] }
  badgeProgress: {}, // Store badge progress as { userId: progressData }
  availableBadges: [], // Store all created badges
  leaderboard: null,
  loading: {
    points: false,
    badges: false,
    badgeProgress: false,
    leaderboard: false,
    creating: false,
    assigning: false,
    deleting: false
  },
  error: null,
  lastUpdated: null,
};

const gamificationSlice = createSlice({
  name: 'gamification',
  initialState,
  reducers: {
    setError: (state, { payload }) => {
      state.error = payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetLoading: (state) => {
      state.loading = {
        points: false,
        badges: false,
        badgeProgress: false,
        leaderboard: false,
        creating: false,
        assigning: false,
        deleting: false
      };
    },
    clearUserData: (state, { payload: userId }) => {
      delete state.points[userId];
      delete state.badges[userId];
      delete state.badgeProgress[userId];
    },
    updateLastUpdated: (state) => {
      state.lastUpdated = new Date().toISOString();
    }
  },
  extraReducers: (builder) => {
    // Fetch Points
    builder
      .addCase(fetchPointsThunk.pending, (state) => {
        state.loading.points = true;
        state.error = null;
      })
      .addCase(fetchPointsThunk.fulfilled, (state, { payload }) => {
        state.points[payload.userId] = payload.points;
        state.loading.points = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchPointsThunk.rejected, (state, { payload }) => {
        state.loading.points = false;
        state.error = payload;
      });

    // Add Points
    builder
      .addCase(addPointsThunk.pending, (state) => {
        state.loading.points = true;
        state.error = null;
      })
      .addCase(addPointsThunk.fulfilled, (state, { payload }) => {
        state.points[payload.userId] = payload.points;
        state.loading.points = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(addPointsThunk.rejected, (state, { payload }) => {
        state.loading.points = false;
        state.error = payload;
      });

    // Create Badge
    builder
      .addCase(createBadgeThunk.pending, (state) => {
        state.loading.creating = true;
        state.error = null;
      })
      .addCase(createBadgeThunk.fulfilled, (state, { payload }) => {
        state.availableBadges.push(payload);
        state.loading.creating = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createBadgeThunk.rejected, (state, { payload }) => {
        state.loading.creating = false;
        state.error = payload;
      });

    // Assign Badge
    builder
      .addCase(assignBadgeThunk.pending, (state) => {
        state.loading.assigning = true;
        state.error = null;
      })
      .addCase(assignBadgeThunk.fulfilled, (state, { payload }) => {
        const { userId, badge } = payload;
        if (!state.badges[userId]) {
          state.badges[userId] = [];
        }
        state.badges[userId].push(badge);
        state.loading.assigning = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(assignBadgeThunk.rejected, (state, { payload }) => {
        state.loading.assigning = false;
        state.error = payload;
      });

    // Fetch User Badges
    builder
      .addCase(fetchUserBadgesThunk.pending, (state) => {
        state.loading.badges = true;
        state.error = null;
      })
      .addCase(fetchUserBadgesThunk.fulfilled, (state, { payload }) => {
        state.badges[payload.userId] = payload.badges;
        state.loading.badges = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchUserBadgesThunk.rejected, (state, { payload }) => {
        state.loading.badges = false;
        state.error = payload;
      });

    // Fetch Badge Progress
    builder
      .addCase(fetchBadgeProgressThunk.pending, (state) => {
        state.loading.badgeProgress = true;
        state.error = null;
      })
      .addCase(fetchBadgeProgressThunk.fulfilled, (state, { payload }) => {
        state.badgeProgress[payload.userId] = payload.progress;
        state.loading.badgeProgress = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchBadgeProgressThunk.rejected, (state, { payload }) => {
        state.loading.badgeProgress = false;
        state.error = payload;
      });

    // Fetch Leaderboard
    builder
      .addCase(fetchLeaderboardThunk.pending, (state) => {
        state.loading.leaderboard = true;
        state.error = null;
      })
      .addCase(fetchLeaderboardThunk.fulfilled, (state, { payload }) => {
        state.leaderboard = payload;
        state.loading.leaderboard = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchLeaderboardThunk.rejected, (state, { payload }) => {
        state.loading.leaderboard = false;
        state.error = payload;
      });

    // Delete Badge
    builder
      .addCase(deleteBadgeThunk.pending, (state) => {
        state.loading.deleting = true;
        state.error = null;
      })
      .addCase(deleteBadgeThunk.fulfilled, (state, { payload }) => {
        state.availableBadges = state.availableBadges.filter(
          badge => badge.id !== payload.badgeId
        );
        // Remove from all users' badges
        Object.keys(state.badges).forEach(userId => {
          state.badges[userId] = state.badges[userId].filter(
            badge => badge.badge_id !== payload.badgeId
          );
        });
        state.loading.deleting = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(deleteBadgeThunk.rejected, (state, { payload }) => {
        state.loading.deleting = false;
        state.error = payload;
      });
  },
});

export const {
  setError,
  clearError,
  resetLoading,
  clearUserData,
  updateLastUpdated
} = gamificationSlice.actions;

export default gamificationSlice.reducer;